import classNames from 'classnames';
import type { FunctionComponent, ReactElement, ReactNode, SVGProps } from 'react';

import type { ButtonSize, ButtonVariant } from './types';

import styles from './BaseButton.module.scss';

export interface ChildRenderProps {
    className: string;
    children: ReactElement;
    disabled: boolean;
    // Using `data-` attribute here to allow passing all of the props to rendered element without errors

    'data-has-text-content': boolean;
}

export interface Props {
    active: boolean;
    block: boolean | 'on-mobile';
    children: ReactNode;
    centered: boolean | 'on-mobile';
    className: string | undefined;
    disabled: boolean;
    render: (props: ChildRenderProps) => ReactElement;
    size: `${ButtonSize}`;
    variant: `${ButtonVariant}`;
    iconClassName: string | undefined;
    iconLeft: FunctionComponent<SVGProps<SVGSVGElement>> | undefined | null;
    iconRight: FunctionComponent<SVGProps<SVGSVGElement>> | undefined | null;
    labelClassName: string | undefined;
}

/**
 * Utility type to aid building a concrete Button component on top of BaseButton.
 * Extends the `BaseButton` Props interface with additional props, resolving any
 * conflicts by preferring `BaseButton` props over the concrete interface props.
 */
export type ExtendedProps<P> = Partial<Omit<Props, 'render'>> & Omit<P, keyof Props>;

export function BaseButton({
    active,
    block = false,
    children,
    centered = block,
    className,
    disabled,
    render,
    size,
    variant,
    iconClassName,
    iconLeft: IconLeft,
    iconRight: IconRight,
    labelClassName,
}: Props) {
    return render({
        className: classNames(className, styles.button, {
            [styles.active]: active,
            [styles.block]: block === true,
            [styles.blockOnMobile]: block === 'on-mobile',
            [styles.centered]: centered === true,
            [styles.centeredOnMobile]: centered === 'on-mobile',
            [styles.disabled]: disabled,

            // variants
            [styles.primary]: variant === 'primary',
            [styles.secondary]: variant === 'secondary',
            [styles.dark]: variant === 'dark',

            // sizes
            [styles.defaultSize]: size === 'default',
            [styles.small]: size === 'small',
            [styles.medium]: size === 'medium',
            [styles.large]: size === 'large',
            [styles.adapt]: size === 'content',
        }),
        disabled,
        children: (
            <>
                {IconLeft && (
                    <IconLeft className={classNames(styles.icon, styles.left, iconClassName)} />
                )}
                {children && (
                    <span className={classNames(styles.label, labelClassName)}>{children}</span>
                )}
                {IconRight && (
                    <IconRight className={classNames(styles.icon, styles.right, iconClassName)} />
                )}
            </>
        ),

        'data-has-text-content': Boolean(children),
    });
}
